// Third Party
import * as React from 'react';
import { ReactElement, cloneElement } from 'react';
import { twMerge } from 'tailwind-merge';

// Utils
import { WithChildrenCommonProps } from '@types';
import { Spinner, SpinnerSize } from '@components/spinner';
import { getButtonComponentStylesAsClassName } from './button-styles';
import { ButtonColor, ButtonSize, ButtonVariant } from './utils';
import cx from 'classnames';

export interface ButtonExtendProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** If the button will take up the full width of its container */
  isFullWidth?: boolean;
  /** Color of the button */
  color?: ButtonColor;
  /** Boolean indicating if boolean is circle shape */
  isCircle?: boolean;
  /** Sets loading state */
  isLoading?: boolean;
  /** Size of the button */
  size?: ButtonSize;
  /** Spinner you want to render when loading if not the default one */
  spinner?: React.ReactNode;
  /** The variant theme for the button */
  variant?: ButtonVariant;
}

export type ButtonProps = WithChildrenCommonProps<ButtonExtendProps>;

export const Button = React.forwardRef(
  (
    props: ButtonProps,
    ref: React.ForwardedRef<HTMLButtonElement>
  ): JSX.Element => {
    const {
      isFullWidth = false,
      children,
      color = 'primary',
      isCircle = false,
      isLoading = false,
      size = 'md',
      spinner = <Spinner size={SpinnerSize.SIZE_SMALL} />,
      variant = 'filled',
      dataId,
      disabled = false,
      type = 'button',
      className: classNameProp,
      ...rest
    } = props;

    const spinnerWithChangedClassName =
      spinner &&
      cloneElement(spinner as ReactElement<{ className?: string }>, {
        className: cx({
          'text-white': variant === 'filled',
          'text-primary-dark': color === 'primary',
          'text-secondary-dark': variant !== 'filled' && color === 'secondary',
          'text-gray-dark': variant !== 'filled' && color === 'gray',
          'text-black': variant !== 'filled' && color === 'black',
        }),
      });

    const Loading = (): JSX.Element => (
      <span className="absolute inset-0 flex items-center justify-center">
        {spinnerWithChangedClassName}
      </span>
    );

    // Arbitrary variant css selector:
    // https://tailwindcss.com/docs/hover-focus-and-other-states#using-arbitrary-variants
    const iconSelectorStyles = '[&_.icon+span]:ml-2 [&_span+.icon]:ml-2';

    const tailwindClassName = twMerge(
      getButtonComponentStylesAsClassName(
        isFullWidth,
        color,
        isCircle,
        size,
        variant,
        disabled,
        isLoading
      ),
      iconSelectorStyles,
      classNameProp
    );

    return (
      <button
        data-id={dataId}
        className={tailwindClassName}
        ref={ref}
        disabled={disabled}
        type={type}
        {...rest}
      >
        <>
          {!!children && children}
          {isLoading && <Loading />}
        </>
      </button>
    );
  }
);

Button.displayName = 'Button';
